import algoliasearch from 'algoliasearch'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import client from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { ALGOLIA_APP_ID, ALGOLIA_SEARCH_PUBLIC_KEY } from 'env'
import isEqual from 'lodash/isEqual'
import { RootState } from 'types'

interface Props {
  children: React.ReactNode
}

const AlgoliaContext = React.createContext({
  algoliaClient: algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_PUBLIC_KEY),
})

export const AlgoliaClientWrapper = ({ children }: Props) => {
  const [algoliaClient, setAlgoliaClient] = useState(() => algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_PUBLIC_KEY))
  const auth = useSelector(({ auth }: RootState) => auth, isEqual)
  const isAuthed = !!auth
  const dispatch = useDispatch()

  useEffect(() => {
    if (isAuthed) {
      // Always fetch new key and merge into auth, as a new key may have been generated
      // with a login to a different session
      client.getUserAlgoliaKey().then((e) => {
        if (e.type === SUCCESS_RESPONSE_TYPE) {
          const newKey = e.data.algolia_api_key
          setAlgoliaClient(algoliasearch(ALGOLIA_APP_ID, newKey))
        }
      })
    }
  }, [isAuthed, dispatch])

  return <AlgoliaContext.Provider value={{ algoliaClient }}>{children}</AlgoliaContext.Provider>
}

export const useAlgoliaClient = () => {
  const context = React.useContext(AlgoliaContext)
  if (context === undefined) {
    throw new Error('useAlgoliaClient must be used within a AlgoliaClientProvider')
  }
  return context.algoliaClient
}

export const AlgoliaClientConsumer = ({ children }: { children: any }) => {
  return (
    <AlgoliaContext.Consumer>
      {(context) => {
        if (context === undefined) {
          throw new Error('AlgoliaClientConsumer must be used within a AlgoliaClientProvider')
        }
        return children(context)
      }}
    </AlgoliaContext.Consumer>
  )
}
